import { type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Skeleton } from '../Skeleton/Skeleton.js';
import styles from './DataQualityState.module.css';

export type QualityState =
  | 'fresh'
  | 'loading'
  | 'empty'
  | 'stale'
  | 'partial'
  | 'unavailable'
  | 'contradicted'
  | 'error'
  | 'permission-denied';

const qualityCopy: Record<QualityState, { title: string; description: string }> = {
  fresh: {
    title: 'Evidence is current',
    description: 'All sources meet freshness policy for this view.',
  },
  loading: {
    title: 'Building the evidence view',
    description: 'Retrieving the latest known-at observations.',
  },
  empty: {
    title: 'No observations in this range',
    description: 'Change the date window or remove a filter.',
  },
  stale: {
    title: 'Source is outside freshness policy',
    description: 'Values remain visible with their last verified timestamp.',
  },
  partial: {
    title: 'Evidence coverage is partial',
    description: 'Unknown dimensions are left open, never scored neutral.',
  },
  unavailable: {
    title: 'Source unavailable',
    description: 'The last trusted value has not been substituted.',
  },
  contradicted: {
    title: 'Sources disagree',
    description: 'Review the competing claims before drawing a conclusion.',
  },
  error: {
    title: 'The panel could not be composed',
    description: 'The underlying evidence remains available in Operations.',
  },
  'permission-denied': {
    title: 'Restricted evidence',
    description: 'Your role can see the claim, but not the private source body.',
  },
};

export function DataQualityState({
  state,
  title,
  description,
  action,
  className,
}: {
  state: QualityState;
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  const copy = qualityCopy[state];

  return (
    <div
      className={clsx(styles.root, styles[state], className)}
      role={state === 'error' ? 'alert' : 'status'}
    >
      {state === 'loading' ? (
        <Skeleton lines={3} />
      ) : (
        <>
          <span className={styles.icon} aria-hidden="true">
            {state === 'empty' ? '○' : '◇'}
          </span>
          <div>
            <strong className={styles.title}>{title ?? copy.title}</strong>
            <p className={styles.description}>{description ?? copy.description}</p>
            {action}
          </div>
        </>
      )}
    </div>
  );
}
